import { useParams, useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { openSignedStorageUrl } from "@/lib/storage";
import { ArrowLeft, CheckCircle, RotateCcw, XCircle, FileText, User } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { TopShopSubmissionScorePanel } from "@/components/topshop/RubricScorer";
import { EvidenceViewer, useChallengeEvidence } from "@/components/topshop/TopShopChallengeEvidence";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function getStudentName(profile: any): string {
  if (!profile) return "Unknown Student";
  if (profile.full_name) return profile.full_name;
  return "Unknown Student";
}

export default function SubmissionReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState("");

  // Fetch submission with all related data
  const { data: submission, isLoading, error } = useQuery({
    queryKey: ["submission-review", id],
    queryFn: async () => {
      if (!id) throw new Error("No submission ID");

      const { data, error } = await supabase
        .from("assignment_submissions")
        .select(`
          *,
          assignment:assignments(
            id,
            title,
            description,
            module:modules(
              id,
              title,
              course:courses(id, title)
            )
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;

      // Fetch profile separately
      let profile = null;
      if (data?.user_id) {
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url")
          .eq("id", data.user_id)
          .single();
        if (!profileError) profile = profileData;
      }

      return { ...data, profile };
    },
    enabled: !!id,
  });

  // Fetch answers for this submission
  const { data: answers } = useQuery({
    queryKey: ["submission-answers", id],
    queryFn: async () => {
      if (!id) return [];

      const { data, error } = await supabase
        .from("assignment_answers")
        .select(`
          *,
          question:assignment_questions(
            id,
            question_text,
            question_type,
            sequence_order
          )
        `)
        .eq("submission_id", id);

      if (error) throw error;
      
      // Sort by question sequence_order client-side since ordering by foreign table alias is not reliable
      const sortedData = (data || []).sort((a, b) => {
        const orderA = a.question?.sequence_order ?? 0;
        const orderB = b.question?.sequence_order ?? 0;
        return orderA - orderB;
      });
      
      return sortedData;
    },
    enabled: !!id,
  });

  // Initialize feedback from existing value.
  // This was a useState lazy initializer, which runs ONCE on first render while
  // `submission` is still undefined — so the textarea stayed empty and saving
  // wrote instructor_feedback: null over the stored feedback.
  useEffect(() => {
    if (submission?.instructor_feedback) {
      setFeedback(submission.instructor_feedback);
    }
  }, [submission?.id, submission?.instructor_feedback]);

  const avatarUrl = useStorageUrl("avatars", submission?.profile?.avatar_url);

  // Top Shop challenge: application and evidence for this submission
  const { data: topShop } = useQuery({
    queryKey: ["submission-review", "top-shop", id, submission?.assignment_id, submission?.user_id],
    enabled: !!submission?.assignment_id && !!submission?.user_id,
    queryFn: async () => {
      const { data: focusAreaId } = await supabase.rpc("top_shop_focus_area_for_assignment", { p_assignment_id: submission!.assignment_id });
      if (!focusAreaId) return null;
      const { data: appId } = await supabase.rpc("get_top_shop_application_id", { p_user_id: submission!.user_id });
      return { focusAreaId: focusAreaId as string, applicationId: (appId as string | null) ?? null };
    },
  });
  const { data: evidence = [] } = useChallengeEvidence(topShop?.applicationId, topShop?.focusAreaId);

  // Review mutation
  const reviewMutation = useMutation({
    mutationFn: async ({ status, feedbackText }: { status: string; feedbackText: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // An UPDATE filtered out by RLS returns no error and zero rows, which is
      // why pressing Pass/Fail on a submission the instructor didn't own used to
      // toast success and change nothing. Verify a row actually changed.
      const { data, error } = await supabase
        .from("assignment_submissions")
        .update({
          status,
          instructor_feedback: feedbackText || null,
          instructor_reviewed_at: new Date().toISOString(),
          instructor_reviewed_by: user.id,
        })
        .eq("id", id)
        .select("id");

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          "You do not have permission to review this submission, so nothing was saved."
        );
      }
    },
    onSuccess: async (_, variables) => {
      if (variables.status === "passed" && topShop?.focusAreaId) {
        const { data, error } = await supabase.functions.invoke("notify-topshop-challenge-reviewed", { body: { submission_id: id } });
        if (!error && (data as any)?.sent) toast.success("Marked as passed and the participant was emailed");
        else toast.warning("Marked as passed. The email could not be sent.");
      } else {
        const statusLabel = variables.status === "passed" ? "Passed" : 
                            variables.status === "retry" ? "Returned for Retry" : "Failed";
        toast.success(`Submission marked as ${statusLabel}`);
      }
      queryClient.invalidateQueries({ queryKey: ["pending-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["pending-review-count"] });
      navigate("/instructor/review-queue");
    },
    onError: (error) => {
      toast.error("Failed to submit review: " + error.message);
    },
  });

  const [confirmUnscored, setConfirmUnscored] = useState(false);

  const allCriteriaScored = async () => {
    if (!topShop?.focusAreaId || !topShop.applicationId) return true;
    const { data: criteria } = await supabase
      .from("program_challenge_criteria")
      .select("id")
      .eq("focus_area_id", topShop.focusAreaId)
      .eq("is_active", true);
    const ids = (criteria ?? []).map((c) => c.id);
    if (ids.length === 0) return true;
    const { data: scores } = await supabase
      .from("program_criterion_scores")
      .select("criterion_id")
      .eq("application_id", topShop.applicationId)
      .in("criterion_id", ids);
    const scored = new Set((scores ?? []).map((s) => s.criterion_id));
    return ids.every((cid) => scored.has(cid));
  };

  const handleReview = async (status: "passed" | "retry" | "failed", skipScoreCheck = false) => {
    if ((status === "retry" || status === "failed") && !feedback.trim()) {
      toast.error("Please provide feedback for retry or fail decisions");
      return;
    }
    if (status === "passed" && topShop?.focusAreaId && !skipScoreCheck && !(await allCriteriaScored())) {
      setConfirmUnscored(true);
      return;
    }
    reviewMutation.mutate({ status, feedbackText: feedback });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64" />
          <Skeleton className="h-48" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !submission) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Link
            to="/instructor/review-queue"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Review Queue
          </Link>

          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-medium mb-2">Submission Not Found</h3>
              <p className="text-muted-foreground mb-4">
                {error?.message || "The submission you're looking for doesn't exist or you don't have access to it."}
              </p>
              <Button onClick={() => navigate("/instructor/review-queue")}>
                Return to Review Queue
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const isAlreadyReviewed = submission.status !== "submitted";

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/instructor/review-queue"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Review Queue
        </Link>

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">{submission.assignment.title}</h1>
          <p className="text-muted-foreground">
            {submission.assignment.module.course.title} → {submission.assignment.module.title}
          </p>
        </div>

        {/* Assignment Instructions */}
        {submission.assignment.description && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-900 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Assignment Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-blue-800 whitespace-pre-wrap">
                {submission.assignment.description}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Student Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Student Submission
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b">
              <Avatar className="h-10 w-10">
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getStudentName(submission.profile).charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{getStudentName(submission.profile)}</p>
                <p className="text-sm text-muted-foreground">
                  Submitted {format(new Date(submission.submitted_at), "PPp")}
                </p>
              </div>
              <Badge
                variant="outline"
                className={
                  submission.status === "passed"
                    ? "ml-auto text-green-600 border-green-200"
                    : submission.status === "failed"
                    ? "ml-auto text-red-600 border-red-200"
                    : submission.status === "retry"
                    ? "ml-auto text-amber-600 border-amber-200"
                    : "ml-auto text-blue-600 border-blue-200"
                }
              >
                {submission.status === "submitted" ? "Pending Review" : submission.status}
              </Badge>
            </div>

            {/* Student Answers */}
            <div className="space-y-4">
              {answers && answers.length > 0 ? (
                answers.map((answer, index) => (
                  <div key={answer.id} className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      Q{index + 1}: {answer.question?.question_text}
                    </p>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm whitespace-pre-wrap">
                        {answer.answer_text || <span className="text-muted-foreground italic">No response</span>}
                      </p>
                      {answer.file_url && (
                        // file_url stores a storage PATH, not a URL (the learner
                        // side comments "Store the path, not the full URL"), and
                        // homework-files is private — so a bare href resolved
                        // in-app and hit the 404 page. Mint a signed URL instead.
                        <button
                          type="button"
                          onClick={async () => {
                            const failure = await openSignedStorageUrl(
                              "homework-files",
                              answer.file_url
                            );
                            if (failure) toast.error(failure);
                          }}
                          className="text-sm text-primary hover:underline mt-2 inline-block"
                        >
                          View attached file →
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground italic">No responses recorded for this submission.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Review Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Instructor Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAlreadyReviewed && submission.instructor_feedback && (
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium mb-1">Previous Feedback:</p>
                <p className="text-sm whitespace-pre-wrap">{submission.instructor_feedback}</p>
              </div>
            )}

            <Textarea
              placeholder="Enter your feedback for the student..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="min-h-32"
              disabled={reviewMutation.isPending}
            />

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={() => handleReview("passed")}
                disabled={reviewMutation.isPending}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Pass
              </Button>
              <Button
                onClick={() => handleReview("retry")}
                disabled={reviewMutation.isPending}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Request Retry
              </Button>
              <Button
                onClick={() => handleReview("failed")}
                disabled={reviewMutation.isPending}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Fail
              </Button>
            </div>
          </CardContent>
        </Card>

        {topShop?.applicationId && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Photos and links</CardTitle></CardHeader>
            <CardContent><EvidenceViewer items={evidence} /></CardContent>
          </Card>
        )}

        <TopShopSubmissionScorePanel userId={submission.user_id} moduleId={submission.assignment?.module?.id} />
      </div>
      <AlertDialog open={confirmUnscored} onOpenChange={setConfirmUnscored}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pass this challenge?</AlertDialogTitle>
            <AlertDialogDescription>
              You have not scored every part of this challenge yet. The participant's email includes their score. Pass it anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back and score</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmUnscored(false); handleReview("passed", true); }}>Pass anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
